import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ifConventionAxios from '../../../api/ifConventionAxios';
import { useParticipantStore } from '../store/participantStore';
import { useParticipants } from '../hooks/useParticipants';
import BackToDashboardButton from '../components/BackToDashboardButton';

const ParticipantSignupPage = () => {
  const history = useHistory();
  const { participants } = useParticipantStore();
  const { loadParticipants } = useParticipants();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Focus on first field with error when validation fails
  useEffect(() => {
    const errorFields = Object.keys(fieldErrors);
    if (errorFields.length > 0) {
      const firstErrorField = errorFields[0];
      // Map field names to input IDs
      const fieldIdMap = {
        'fullName': 'fullName',
        'loftName': 'loftName', 
        'emailAddress': 'emailAddress',
        'mobile': 'mobile',
        'role': 'roleHandler', // Focus on first role checkbox
        'section': 'section',
        'totalBirds': 'totalBirds',
        'distanceFromRaceStation': 'distanceFromRaceStation',
        'breederTotalBirds': 'breederTotalBirds'
      };
      
      const elementId = fieldIdMap[firstErrorField];
      if (elementId) {
        // Use setTimeout to ensure DOM updates are complete
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 100);
      }
    }
  }, [fieldErrors]);

  // Load participants on mount to ensure we have data for duplicate checking
  useEffect(() => {
    const loadData = async () => {
      try {
        await loadParticipants();
      } catch (err) {
      }
    };
    loadData();
  }, [loadParticipants]);
  
  const [formData, setFormData] = useState({
    fullName: '',
    loftName: '',
    emailAddress: '',
    mobile: '',
    role: [],
    section: '',
    totalBirds: '',
    distanceFromRaceStation: '',
    breederApplicationMethod: ['in-app'],
    breederTotalBirds: '',
    hideContactInfo: false,
    preferredNotificationMethod: ['email']
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    // Clear field-specific error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRoleChange = (role) => {
    setFormData(prev => {
      let roles;
      
      if (role === 'handler') {
        if (prev.role.includes('handler')) {
          // Unchecking handler - remove handler but keep breeder if it was selected
          roles = prev.role.filter(r => r !== 'handler');
        } else {
          // Checking handler - add both handler and breeder (requirement)
          roles = [...new Set([...prev.role, 'handler', 'breeder'])];
        }
      } else if (role === 'breeder') {
        if (prev.role.includes('breeder')) {
          // Unchecking breeder - remove breeder and handler (since handler requires breeder)
          roles = prev.role.filter(r => r !== 'breeder' && r !== 'handler');
        } else {
          // Checking breeder - just add breeder
          roles = [...prev.role, 'breeder'];
        }
      } else {
        // Default behavior for other roles
        roles = prev.role.includes(role)
          ? prev.role.filter(r => r !== role)
          : [...prev.role, role];
      }
      
      return { ...prev, role: roles };
    });
  };

  const handleNotificationMethodChange = (method) => {
    setFormData(prev => {
      const methods = prev.preferredNotificationMethod.includes(method)
        ? prev.preferredNotificationMethod.filter(m => m !== method)
        : [...prev.preferredNotificationMethod, method];
      return { ...prev, preferredNotificationMethod: methods };
    });
  };

  const handleBreederMethodChange = (method) => {
    setFormData(prev => {
      const methods = prev.breederApplicationMethod.includes(method)
        ? prev.breederApplicationMethod.filter(m => m !== method)
        : [...prev.breederApplicationMethod, method];
      return { ...prev, breederApplicationMethod: methods };
    });
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
      isValid = false;
    }
    
    if (!formData.emailAddress.trim() && !formData.mobile.trim()) {
      errors.emailAddress = 'Email or mobile phone is required';
      errors.mobile = 'Email or mobile phone is required';
      isValid = false;
    }
    
    if (formData.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      errors.emailAddress = 'Please enter a valid email address';
      isValid = false;
    }
    
    // Validate notification method matches provided contact info
    if (formData.preferredNotificationMethod.includes('email') && !formData.emailAddress.trim()) {
      errors.emailAddress = 'Email is required for email notifications';
      isValid = false;
    }
    if (formData.preferredNotificationMethod.includes('text') && !formData.mobile.trim()) {
      errors.mobile = 'Mobile phone is required for SMS notifications';
      isValid = false;
    }
    
    if (formData.role.length === 0) {
      errors.role = 'Please select at least one role';
      isValid = false;
    }
    
    if (formData.role.includes('handler')) {
      if (!formData.section) {
        errors.section = 'Section is required for handlers';
        isValid = false;
      }
      if (!formData.totalBirds || parseInt(formData.totalBirds) < 5) {
        errors.totalBirds = 'Handlers must accept at least 5 birds';
        isValid = false;
      }
      // Validate mobile phone is required when "call" method is selected
      if (formData.breederApplicationMethod.includes('call') && !formData.mobile.trim()) {
        errors.mobile = 'Mobile phone is required when breeders must call you';
        isValid = false;
      }
    }
    
    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      // Check if email or mobile already exists using participants from store
      
      const errors = {};

      // Check for duplicate email (if provided)
      if (formData.emailAddress.trim()) {
        const emailToCheck = formData.emailAddress.trim().toLowerCase();
        const emailExists = participants.some(p => {
          const participantEmail = p.emailAddress ? p.emailAddress.toLowerCase() : '';
          return participantEmail === emailToCheck;
        });
        if (emailExists) {
          errors.emailAddress = 'This email address is already registered';
        }
      }

      // Check for duplicate mobile (if provided)
      if (formData.mobile.trim()) {
        const mobileToCheck = formData.mobile.trim().replace(/\D/g, '');
        const mobileExists = participants.some(p => {
          const participantMobile = p.mobile ? p.mobile.replace(/\D/g, '') : '';
          return participantMobile === mobileToCheck;
        });
        if (mobileExists) {
          errors.mobile = 'This mobile number is already registered';
        }
      }

      // If duplicates found, show errors and stop
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('An account with this email or phone number already exists. Please use different contact information or login if you already have an account.');
        setLoading(false);
        return;
      }

      const endpoint = 'Convention/AddParticipant';
      
      // Prepare the data - match the exact structure of existing participants
      const submitData = {
        lastUpdatedDate: new Date().toISOString(),
        fullName: formData.fullName.trim(),
        loftName: formData.loftName.trim() || '',
        emailAddress: formData.emailAddress.trim() || '',
        mobile: formData.mobile.trim() || '',
        role: formData.role,
        hideContactInfo: formData.hideContactInfo,
        preferredNotificationMethod: formData.preferredNotificationMethod
      };

      // Add handler-specific info
      if (formData.role.includes('handler')) {
        submitData.handlerInfo = {
          section: formData.section,
          totalBirds: parseInt(formData.totalBirds) || 0,
          reserved: 0,
          openSlots: parseInt(formData.totalBirds) || 0,
          breederApplicationMethod: formData.breederApplicationMethod,
          distanceFromRaceStation: formData.distanceFromRaceStation.trim() || null,
          requireBreederCall: false,
          breeders: []
        };
      } else {
        submitData.handlerInfo = null;
      }

      // Add breeder-specific info
      if (formData.role.includes('breeder')) {
        submitData.breederInfo = {
          handlerName: null,
          assignedBirds: [],
          totalBirdsForRace: parseInt(formData.breederTotalBirds) || 0
        };
      } else {
        submitData.breederInfo = null;
      }

      const response = await ifConventionAxios.post(endpoint, submitData);

      // Server returns 201 Created with the participant data (including ID)
      if (response.status === 201) {
        
        // Reload participants from API to include the newly registered participant
        await loadParticipants(true); // Force refresh
        
        setSuccess(true);
        // No automatic redirect - user must click OK button
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (err) {
      
      // Check if it's a duplicate error from the server
      const errorMessage = err.response?.data?.message || err.message || '';
      if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('exist')) {
        setFieldErrors({ emailAddress: 'This email address is already registered' });
        setError('An account with this email already exists.');
      } else if (errorMessage.toLowerCase().includes('mobile') && errorMessage.toLowerCase().includes('exist')) {
        setFieldErrors({ mobile: 'This mobile number is already registered' });
        setError('An account with this phone number already exists.');
      } else {
        setError(errorMessage || 'Failed to register. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8">
            <div className="card shadow-sm border-0">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h2 className="text-success text-center mb-4">Registration Successful!</h2>
                <p className="text-center mb-4">
                  Welcome to the 2026 IF Convention Race! You can now manage your own profile and bird assignments:
                </p>
                
                <div className="alert alert-info mb-4">
                  <ul className="mb-0">
                    <li><strong>Edit your profile:</strong> Update your contact info, loft name, and handler details</li>
                    <li><strong>Request handler placement:</strong> Submit requests to handlers with available slots</li>
                    <li><strong>Handler approvals:</strong> Review, approve, or decline breeder requests</li>
                    <li><strong>Track birds:</strong> Monitor your bird assignments and handler capacity</li>
                    <li><strong>Real-time updates:</strong> All changes are reflected immediately in the directory</li>
                  </ul>
                </div>

                <p className="text-center text-muted mb-4">
                  To get started, you'll need to login to access your Account Center.
                </p>

                <div className="text-center">
                  <button 
                    className="btn btn-primary btn-lg"
                    onClick={() => history.push('/race/select-login')}
                  >
                    <i className="bi bi-check-lg me-2"></i>
                    OK - Go to Participant Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <BackToDashboardButton />
      </div>
    );
  }

  return (

    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4 p-md-5">
              {/* Header */}
              <div className="text-center mb-5">
                <div 
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #0b3d91 0%, #1e5bb8 100%)' }}
                >
                  <i className="bi bi-person-plus-fill text-white" style={{ fontSize: '2rem' }}></i>
                </div>
                <h2 style={{ color: '#0b3d91' }}>2026 IF Convention Race Registration</h2>
                <p className="text-muted mb-3">Sign up as a Handler, Breeder, or both</p>
                
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <a href="/race/details" className="btn btn-outline-primary btn-sm">
                    <i className="bi bi-info-circle me-1"></i>
                    View Race Details
                  </a>
                  <a href="/race/participants" className="btn btn-outline-secondary btn-sm">
                    <i className="bi bi-people me-1"></i>
                    View Participants
                  </a>
                  <a href="/race" className="btn btn-outline-secondary btn-sm">
                    <i className="bi bi-house me-1"></i>
                    Back to Race Home
                  </a>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#0b3d91' }}>
                    <i className="bi bi-person-circle me-2"></i>
                     Basic Information <span className="text-danger">*</span>
                  </h5>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Full Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        id="fullName"
                        className={`form-control ${fieldErrors.fullName ? 'is-invalid' : ''}`}
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        placeholder="John Doe"
                        required
                      />
                      {fieldErrors.fullName && (
                        <div className="invalid-feedback d-block">
                          {fieldErrors.fullName}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Loft Name</label>
                      <input
                        type="text"
                        id="loftName"
                        className="form-control"
                        value={formData.loftName}
                        onChange={(e) => handleInputChange('loftName', e.target.value)}
                        placeholder="Enter your loft name"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Email Address</label>
                      <input
                        type="email"
                        id="emailAddress"
                        className={`form-control ${fieldErrors.emailAddress ? 'is-invalid' : ''}`}
                        value={formData.emailAddress}
                        onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                        placeholder="your@email.com"
                      />
                      {fieldErrors.emailAddress && (
                        <div className="invalid-feedback d-block">
                          {fieldErrors.emailAddress}
                        </div>
                      )}
                      {!fieldErrors.emailAddress && (
                        <small className="text-muted d-block">Used for login and notifications</small>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Mobile Phone</label>
                      <input
                        type="tel"
                        id="mobile"
                        className={`form-control ${fieldErrors.mobile ? 'is-invalid' : ''}`}
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                        placeholder="908-123-4567"
                      />
                      {fieldErrors.mobile && (
                        <div className="invalid-feedback d-block">
                          {fieldErrors.mobile}
                        </div>
                      )}
                      {!fieldErrors.mobile && (
                        <small className="text-muted d-block">Used for login and notifications</small>
                      )}
                    </div>
                  </div>
                </div>

                {/* Role Selection */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#0b3d91' }}>
                    <i className="bi bi-clipboard-check me-2"></i>
                    Your Role <span className="text-danger">*</span>
                  </h5>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="roleHandler"
                      checked={formData.role.includes('handler')}
                      onChange={() => handleRoleChange('handler')}
                    />
                    <label className="form-check-label fw-bold" htmlFor="roleHandler">
                      Handler (also required to participate as a breeder)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="roleBreeder"
                      checked={formData.role.includes('breeder')}
                      onChange={() => handleRoleChange('breeder')}
                    />
                    <label className="form-check-label fw-bold" htmlFor="roleBreeder">
                      Breeder
                    </label>
                  </div>
                  {fieldErrors.role && (
                    <div className="text-danger small mt-2">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      {fieldErrors.role}
                    </div>
                  )}
                </div>

                {/* Handler-Specific Fields */}
                {formData.role.includes('handler') && (
                  <div className="mb-4 p-3 bg-light rounded">
                    <h5 className="mb-3" style={{ color: '#0b3d91' }}>
                      <i className="bi bi-house-door me-2"></i>
                      Handler Information
                    </h5>
                    
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Section <span className="text-danger">*</span></label>
                        <select
                          id="section"
                          className={`form-select ${fieldErrors.section ? 'is-invalid' : ''}`}
                          value={formData.section}
                          onChange={(e) => handleInputChange('section', e.target.value)}
                          required={formData.role.includes('handler')}
                        >
                          <option value="">Select Section</option>
                          <option value="North">North</option>
                          <option value="Central">Central</option>
                          <option value="South">South</option>
                        </select>
                        {fieldErrors.section && (
                          <div className="invalid-feedback d-block">
                            {fieldErrors.section}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Total Birds Capacity <span className="text-danger">*</span></label>
                        <input
                          type="number"
                          id="totalBirds"
                          className={`form-control ${fieldErrors.totalBirds ? 'is-invalid' : ''}`}
                          min="5"
                          value={formData.totalBirds}
                          onChange={(e) => handleInputChange('totalBirds', e.target.value)}
                          placeholder="Minimum 5"
                          required={formData.role.includes('handler')}
                        />
                        {fieldErrors.totalBirds && (
                          <div className="invalid-feedback d-block">
                            {fieldErrors.totalBirds}
                          </div>
                        )}
                        {!fieldErrors.totalBirds && (
                          <small className="text-muted d-block">Minimum 5 birds</small>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Distance (miles)</label>
                        <input
                          type="number"
                          id="distanceFromRaceStation"
                          className="form-control"
                          min="0"
                          step="any"
                          value={formData.distanceFromRaceStation}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow numbers with optional decimal (any number of decimal places)
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              handleInputChange('distanceFromRaceStation', value);
                            }
                          }}
                          placeholder="e.g., 10.5 or 500.121564"
                        />
                        <small className="text-muted d-block">Enter distance in miles</small>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">I prefer the breeders to call me to handle their birds</label>                                            
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="methodCall"
                          checked={formData.breederApplicationMethod.includes('call')}
                          onChange={() => handleBreederMethodChange('call')}
                        />
                        <label className="form-check-label" htmlFor="methodCall">
                          Call (Breeders must call first to reserve a slot before submitting an in-app request.)
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Breeder-Specific Fields */}
                {formData.role.includes('breeder') && (
                  <div className="mb-4 p-3 bg-light rounded">
                    <h5 className="mb-3" style={{ color: '#0b3d91' }}>
                      <i className="bi bi-egg me-2"></i>
                      Breeder Information
                    </h5>
                    
                    <div className="mb-3">
                      <label className="form-label fw-bold">How many of your own birds are you planning to enter to the race?</label>
                      <input
                        type="number"
                        id="breederTotalBirds"
                        className="form-control"
                        min="0"
                        value={formData.breederTotalBirds}
                        onChange={(e) => handleInputChange('breederTotalBirds', e.target.value)}
                        placeholder="Enter number of birds"
                      />
                      <small className="text-muted d-block">You can update this later in your Account Center</small>
                    </div>
                  </div>
                )}

                {/* Notifications */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#0b3d91' }}>
                    <i className="bi bi-bell me-2"></i>
                    Notification Preferences
                  </h5>
                  
                  <div className="mb-3">
                    <label className="form-label fw-bold">How would you like to receive notifications?</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="notifyEmail"
                        checked={formData.preferredNotificationMethod.includes('email')}
                        onChange={() => handleNotificationMethodChange('email')}
                      />
                      <label className="form-check-label" htmlFor="notifyEmail">
                        Email
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="notifyText"
                        checked={formData.preferredNotificationMethod.includes('text')}
                        onChange={() => handleNotificationMethodChange('text')}
                      />
                      <label className="form-check-label" htmlFor="notifyText">
                        Text/SMS
                      </label>
                    </div>
                  </div>
                </div>

                {/* Privacy Options */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#0b3d91' }}>
                    <i className="bi bi-shield-lock me-2"></i>
                    Privacy Options
                  </h5>
                  
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="hideContact"
                      checked={formData.hideContactInfo}
                      onChange={(e) => handleInputChange('hideContactInfo', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="hideContact">
                      Hide my contact information from public view
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => history.push('/race/participants')}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Registering...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Complete Registration
                      </>
                    )}
                  </button>
                </div>

                {/* Login Link */}
                <div className="text-center mt-4 pt-3 border-top">
                  <p className="text-muted mb-0">
                    Already have an account?{' '}
                    <a 
                      href="/race/participants" 
                      className="fw-bold text-decoration-none"
                      style={{ color: '#0b3d91' }}
                    >
                      Go to Participants List to Login
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <BackToDashboardButton />
    </div>
  );
};

export default ParticipantSignupPage;
